/**
 * Hand summary image export (2D drawing surface) — zero runtime deps.
 * Soft-outside 2.3: PNG story follows shareViewModel (line→holes→board;
 * hide empty layers — never fabricate em-dash).
 * Soft Quiet C1: denser street chronology is UI-only (processMore) — not PNG.
 */
import {
  formatBoardProSpaced,
  formatHolePro,
} from "./notation"
import { buildShareViewModel } from "./shareViewModel"
import { formatBlindsNotation, formatStackNotation } from "./lineText"
import { formatPotDisplay } from "./potLabel"
import type { Card, HandState, Suit } from "./types"

const SUIT_SYM: Record<Suit, string> = {
  s: "\u2660",
  h: "\u2665",
  d: "\u2666",
  c: "\u2663",
}

const SUIT_COLOR: Record<Suit, string> = {
  s: "#e8e8e8",
  h: "#f87171",
  d: "#60a5fa",
  c: "#4ade80",
}

export type ImageExportLabels = {
  brand: string
  footer: string
  holesHeader: string
  boardHeader: string
  actionsHeader: string
  /** Softwise M10b: the single line text (lib/lineText) — same as Share card. */
  lineText?: string
  /** Amber incomplete badge under brand when force-sharing an incomplete hand. */
  incompleteBadge?: string
  /** Section header for optional manual takeaway (complete hands only). */
  takeawayHeader?: string
  /** Muted HU/multiway process line (complete hands only). */
  processLine?: string
  /** Softwise M10d: localized chip word for the single stack notation. */
  stackChipsUnit?: string
  /** Softwise M10d: Share-card settings line (`6명 · 0.5/1 · BTN · 100bb`). */
  metaHead?: string
  /** Muted Reviewed · datetime stamp (complete hands; set at export time). */
  reviewedLine?: string
  /**
   * Soft G: optional ending line — drawn once right after the last process
   * line, at meta-line size/tone. Absent → no op (byte-identical PNG input).
   */
  endingLine?: string
}

const LINE_FONT = "600 14px ui-sans-serif, system-ui, sans-serif"

/** Wrap the line text at ` · ` joints to fit `maxW` (measured). */
function wrapLineText(text: string, maxW: number): string[] {
  const probe = document.createElement("canvas").getContext("2d")
  const measure = (s: string) => {
    if (!probe) return s.length * 8
    probe.font = LINE_FONT
    return probe.measureText(s).width
  }
  const parts = text.split(" · ")
  const lines: string[] = []
  let cur = ""
  for (const p of parts) {
    const next = cur ? cur + " · " + p : p
    if (cur && measure(next) > maxW) {
      lines.push(cur + " ·")
      cur = p
    } else {
      cur = next
    }
  }
  if (cur) lines.push(cur)
  return lines
}

function seatCode(pos: string, playerCount: number): string {
  if (playerCount === 2 && pos === "BTN") return "BTN/SB"
  return pos
}

function tablePositions(playerCount: number): string[] {
  const count = Math.min(9, Math.max(2, playerCount))
  if (count === 2) return ["BTN", "BB"]
  const n = Math.max(0, count - 3)
  let mid: string[] = []
  if (n === 1) mid = ["UTG"]
  else if (n === 2) mid = ["UTG", "CO"]
  else if (n === 3) mid = ["UTG", "MP", "CO"]
  else if (n === 4) mid = ["UTG", "MP", "HJ", "CO"]
  else if (n === 5) mid = ["UTG", "UTG+1", "MP", "HJ", "CO"]
  else if (n > 5) mid = ["UTG", "UTG+1", "MP", "MP+1", "HJ", "CO"].slice(0, n)
  return [...mid, "BTN", "SB", "BB"]
}

function holeLine(hole: [Card | null, Card | null]): string {
  const h = formatHolePro(hole)
  // Soft-outside 2.3 / empty-layer: never fabricate em-dash on PNG.
  if (!h.full) return ""
  return h.shorthand ? h.full + " (" + h.shorthand + ")" : h.full
}

function prettyCard(card: Card): { text: string; color: string } {
  const rank = card[0]!
  const suit = card[1] as Suit
  return { text: rank + SUIT_SYM[suit], color: SUIT_COLOR[suit] }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + w, y, x + w, y + h, radius)
  ctx.arcTo(x + w, y + h, x, y + h, radius)
  ctx.arcTo(x, y + h, x, y, radius)
  ctx.arcTo(x, y, x + w, y, radius)
  ctx.closePath()
}

function drawCardFace(
  ctx: CanvasRenderingContext2D,
  card: Card | null,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  // Restore align/baseline after drawing: later left-aligned text must not
  // inherit `center` (PNG left-clip bug).
  ctx.save()
  try {
    drawCardFaceInner(ctx, card, x, y, w, h)
  } finally {
    ctx.restore()
  }
}

function drawCardFaceInner(
  ctx: CanvasRenderingContext2D,
  card: Card | null,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  roundRect(ctx, x, y, w, h, 6)
  ctx.fillStyle = "#161616"
  ctx.fill()
  ctx.strokeStyle = "#3a3a3a"
  ctx.lineWidth = 1
  ctx.stroke()
  if (!card) {
    ctx.fillStyle = "#a3a3a3"
    ctx.font = "600 14px ui-sans-serif, system-ui, sans-serif"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText("?", x + w / 2, y + h / 2)
    return
  }
  const { text, color } = prettyCard(card)
  ctx.fillStyle = color
  ctx.font = "700 16px ui-sans-serif, system-ui, sans-serif"
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.fillText(text, x + w / 2, y + h / 2)
}

/** Build a PNG blob of the hand summary card. */
export async function buildHandImageBlob(
  state: HandState,
  labels: ImageExportLabels,
): Promise<Blob> {
  const W = 720
  const pad = 28
  const cardW = 52
  const cardH = 72
  const lineH = 22
  const positions = tablePositions(state.playerCount)
  const villainHoles: { pos: string; hole: [Card | null, Card | null] }[] = []
  for (const pos of positions) {
    if (pos === state.heroPosition) continue
    const holes = state.knownHoles?.[pos]
    if (!holes || (!holes[0] && !holes[1])) continue
    villainHoles.push({ pos, hole: holes })
  }
  // Soft-safe Move 1: PNG story layers from share view-model (no process tags).
  const svm = buildShareViewModel(state)
  const boardCards = svm.boardCards
  // Soft empty-layer: omit actions when none (no em-dash placeholder)
  // Softwise M10b: PNG prints the single line text, wrapped at ` · ` joints.
  const actionLines =
    svm.layers.line && labels.lineText
      ? wrapLineText(labels.lineText, W - pad * 2)
      : []
  let estimate = pad + 28 + 24 + 22 + 18
  if (labels.incompleteBadge) estimate += 22
  if (labels.processLine) estimate += 16
  if (actionLines.length > 0) {
    estimate += 16 + 20 + actionLines.length * lineH + 8
  }
  // Soft Quiet C1: no denser timeline on PNG (UI processMore only).
  // Suit PR (Poker): the PNG never drops the holes section. When the hero's
  // holes are not recorded it draws the seat with 「?」 faces (the gap stays
  // visible; nothing is guessed). Share card / Copy keep their own layers.
  const drawHoles = svm.layers.holes || !state.holeCards[0] || !state.holeCards[1]
  // Soft-outside 2.3: height follows Share story — omit empty holes/board layers.
  if (drawHoles) {
    estimate += 20 + cardH + 26
    estimate += villainHoles.length * (cardH + 34)
  }
  if (svm.layers.board && boardCards.length > 0) {
    estimate += 16 + 20 + cardH + 26
  }
  estimate += 36 + pad
  // Same story as Share/Copy: VM suppresses takeaway when incomplete.
  const takeawayText = svm.takeaway ?? ''
  if (takeawayText) estimate += 16 + 20 + 28
  if (labels.reviewedLine) estimate += 20
  if (labels.endingLine) estimate += 20
  const H = Math.max(480, estimate)
  const surface = document.createElement("canvas")
  surface.width = W
  surface.height = H
  const ctx = surface.getContext("2d")
  if (!ctx) throw new Error("2d context unavailable")

  const grad = ctx.createLinearGradient(0, 0, 0, H)
  grad.addColorStop(0, "#1c1c1c")
  grad.addColorStop(1, "#0e0e0e")
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, W, H)
  // Hairline card edge — physical card presence, not collector-card chrome
  ctx.strokeStyle = "#454545"
  ctx.lineWidth = 2
  roundRect(ctx, 1.5, 1.5, W - 3, H - 3, 18)
  ctx.stroke()
  ctx.strokeStyle = "rgba(255, 255, 255, 0.06)"
  ctx.lineWidth = 1
  roundRect(ctx, 3.5, 3.5, W - 7, H - 7, 16)
  ctx.stroke()

  let y = pad
  ctx.textAlign = "left"
  ctx.textBaseline = "top"
  ctx.fillStyle = "#858585"
  ctx.font = "600 12px ui-sans-serif, system-ui, sans-serif"
  ctx.fillText(labels.brand.toUpperCase(), pad, y)
  y += 22

  if (labels.incompleteBadge) {
    // Amber warning badge — do not imply a complete takeaway.
    // Same chip as the on-screen .shareIncompleteBadge (dark text on amber).
    ctx.font = "800 12px ui-sans-serif, system-ui, sans-serif"
    const badge = labels.incompleteBadge.toUpperCase()
    const bw = ctx.measureText(badge).width
    roundRect(ctx, pad - 8, y - 3, bw + 16, 18, 9)
    ctx.fillStyle = "#fbbf24"
    ctx.fill()
    ctx.strokeStyle = "#f59e0b"
    ctx.lineWidth = 1
    ctx.stroke()
    ctx.fillStyle = "#1c1917"
    ctx.fillText(badge, pad, y)
    y += 22
  }

  // Softwise M10d: the single blinds / stack notation (lib/lineText).
  const blinds = formatBlindsNotation(state)
  // Softwise M10c: amount only — processLine is the one HU / multiway label.
  const pot = formatPotDisplay(state.potBb)
  const metaBits = [
    labels.metaHead ??
      [state.playerCount + "-max", blinds, seatCode(state.heroPosition, state.playerCount), formatStackNotation(state, labels.stackChipsUnit)].join(" · "),
  ]
  if (state.effectiveStackBb != null && Number.isFinite(state.effectiveStackBb)) {
    metaBits.push("eff " + state.effectiveStackBb + "bb")
  }
  if (pot.combinedPro) metaBits.push(pot.combinedPro)

  // Soft #2: skip meta/process at top — draw after story, before stamp.
  // Light rule under brand so Actions still read as the first story block.
  ctx.strokeStyle = "#242424"
  ctx.beginPath()
  ctx.moveTo(pad, y)
  ctx.lineTo(W - pad, y)
  ctx.stroke()
  y += 16

  // Chronology first (matches Share card L1): actions before hole/board.
  // Soft empty-layer: omit actions when none (no em-dash placeholder)
  ctx.fillStyle = "#858585"
  ctx.font = "600 12px ui-sans-serif, system-ui, sans-serif"
  if (actionLines.length > 0) {
    ctx.beginPath()
    ctx.fillText(labels.actionsHeader.toUpperCase(), pad, y)
    y += 18
    ctx.fillStyle = "#f0f0f0"
    ctx.font = LINE_FONT
    for (const line of actionLines) {
      ctx.fillText(line, pad, y)
      y += lineH
    }
  }

  // Soft Quiet C1: denser street chronology stays UI-only — not PNG.

  y += 8
  const drawHoleBlock = (
    label: string,
    hole: [Card | null, Card | null],
  ) => {
    // Poker lock: hero position not recorded → no seat label, only the faces.
    if (label) {
      ctx.fillStyle = "#a3a3a3"
      ctx.font = "600 12px ui-sans-serif, system-ui, sans-serif"
      ctx.fillText(label, pad, y)
      y += 16
    }
    drawCardFace(ctx, hole[0], pad, y, cardW, cardH)
    drawCardFace(ctx, hole[1], pad + cardW + 8, y, cardW, cardH)
    y += cardH + 6
    const caption = holeLine(hole)
    if (caption) {
      ctx.fillStyle = "#858585"
      ctx.font = "500 12px ui-monospace, Menlo, monospace"
      ctx.fillText(caption, pad, y)
      y += 20
    } else {
      y += 8
    }
  }

  // Soft-outside 2.3 L1: holes layer matches Share/Copy — hide when empty (no —).
  if (drawHoles) {
    ctx.beginPath()
    ctx.fillStyle = "#858585"
    ctx.font = "600 12px ui-sans-serif, system-ui, sans-serif"
    ctx.fillText(labels.holesHeader.toUpperCase(), pad, y)
    y += 18
    drawHoleBlock(
      seatCode(state.heroPosition, state.playerCount),
      state.holeCards,
    )
    for (const v of villainHoles) {
      drawHoleBlock(seatCode(v.pos, state.playerCount), v.hole)
    }
  }

  // Soft empty-layer: omit board block when empty (no em-dash placeholder)
  // Softwise M10e: clear any leftover card path so the board title never
  // inherits a hairline stroke that looked like an underline.
  if (svm.layers.board && boardCards.length > 0) {
    y += 8
    ctx.beginPath()
    ctx.fillStyle = "#858585"
    ctx.font = "600 12px ui-sans-serif, system-ui, sans-serif"
    ctx.fillText(labels.boardHeader.toUpperCase(), pad, y)
    y += 18
    let x = pad
    for (const c of boardCards) {
      drawCardFace(ctx, c, x, y, cardW, cardH)
      x += cardW + 8
    }
    y += cardH + 6
    const boardPro = formatBoardProSpaced(state)
    if (boardPro) {
      ctx.fillStyle = "#858585"
      ctx.font = "500 12px ui-monospace, Menlo, monospace"
      ctx.fillText(boardPro, pad, y)
      y += 20
    }
  }

  // Manual takeaway only — never invent text; skip when incomplete.
  if (takeawayText) {
    y += 10
    ctx.fillStyle = "#858585"
    ctx.font = "600 12px ui-sans-serif, system-ui, sans-serif"
    ctx.fillText(
      (labels.takeawayHeader ?? "Takeaway").toUpperCase(),
      pad,
      y,
    )
    y += 18
    ctx.fillStyle = "#e8e8e8"
    ctx.font = "600 15px ui-sans-serif, system-ui, sans-serif"
    // Single-line clip to card width
    const maxW = W - pad * 2
    let line = takeawayText
    if (ctx.measureText(line).width > maxW) {
      while (line.length > 1 && ctx.measureText(line + "…").width > maxW) {
        line = line.slice(0, -1)
      }
      line = line + "…"
    }
    ctx.fillText(line, pad, y)
    y += 22
  }

  // Soft G: one ending line after the story — same font/tone as the meta line
  // (never larger / brighter; no icon, box or colour). Only when pressed.
  if (labels.endingLine) {
    y += 6
    ctx.fillStyle = "#858585"
    ctx.font = "500 12px ui-sans-serif, system-ui, sans-serif"
    ctx.fillText(labels.endingLine, pad, y)
    y += 14
  }

  y += 12
  ctx.strokeStyle = "#242424"
  ctx.beginPath()
  ctx.moveTo(pad, y)
  ctx.lineTo(W - pad, y)
  ctx.stroke()
  y += 10
  // L2/L3 chrome after story (matches Share card foot)
  ctx.fillStyle = "#858585"
  ctx.font = "500 12px ui-sans-serif, system-ui, sans-serif"
  ctx.fillText(metaBits.join(" \u00b7 "), pad, y)
  y += 18
  if (labels.processLine) {
    ctx.fillStyle = "#858585"
    ctx.font = "500 12px ui-sans-serif, system-ui, sans-serif"
    ctx.fillText(labels.processLine, pad, y)
    y += 16
  }
  if (labels.reviewedLine) {
    // L3 muted stamp near footer — does not compete with takeaway/line
    ctx.fillStyle = "#858585"
    ctx.font = "500 12px ui-sans-serif, system-ui, sans-serif"
    ctx.fillText(labels.reviewedLine, pad, y)
    y += 16
  }
  ctx.fillStyle = "#858585"
  ctx.font = "500 12px ui-sans-serif, system-ui, sans-serif"
  ctx.fillText(labels.footer, pad, y)

  return await new Promise<Blob>((resolve, reject) => {
    surface.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error("toBlob failed"))
      },
      "image/png",
      0.92,
    )
  })
}
